// src/lib/telegramInitData.ts
// Единственное место, которое решает, какую Telegram initData можно отправить на сервер.
//
// Зачем отдельный модуль: вне Telegram `useTelegram` отдаёт мок-данные из
// `src/lib/mockTelegram.ts`, подписанные строкой `mock-hash`. Сервер такую подпись
// отвергает (`verifyTelegramInitData` → 401 «Invalid Telegram signature»), а фронт
// показывал вместо страницы экран «Ошибка аутентификации».
// Боевой случай 14–20.08.2026: 66 отказов в `auth_request_log`, все с `telegram_id = null`,
// на них падал вход по ссылке-приглашению в обычном браузере.
// Проверка «это мок» раньше жила копиями в трёх файлах — расхождение копий означало бы,
// что мок снова уезжает в прод.

export function isMockInitData(raw: unknown): boolean {
  if (typeof raw !== 'string' || raw.length === 0) return false
  const hash = new URLSearchParams(raw).get('hash')
  return hash === 'mock-hash' || hash === 'mock_hash'
}

export interface InitDataSources {
  /** `window.Telegram.WebApp.initData` — вне Telegram это пустая строка. */
  webAppInitData?: string | null
  /** `initDataRaw` из `retrieveLaunchParams()` (@telegram-apps/sdk). */
  launchFallbackRaw?: string | null
  /** true только в dev-сборке: там мок подписывается настоящим ботом и вход по нему легален. */
  allowMock: boolean
}

/**
 * Возвращает initData, пригодную для отправки в `/auth`, либо null — тогда звать сервер
 * нечем и надо просто показать экран входа.
 *
 * Пустая строка кандидатом не считается: в Telegram бывает, что `WebApp.initData` пуст,
 * а launch params валидны. Прежний код склеивал источники через `??`, поэтому пустая
 * строка побеждала рабочий фолбэк.
 */
export function resolveSubmittableInitData(sources: InitDataSources): string | null {
  const candidates = [sources.webAppInitData, sources.launchFallbackRaw]

  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || candidate.length === 0) continue
    if (isMockInitData(candidate) && !sources.allowMock) continue
    return candidate
  }

  return null
}
